// Iter 134 — Environment drift report.
//
// Preview and production have separate databases, so accounts drift apart.
// This page exports a snapshot of the current environment and diffs it
// against a snapshot pasted in from the other one — no cross-environment
// credentials, the operator carries the file across.

import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import {
  GitCompareArrows, Download, ClipboardCopy, Upload, Loader2, Users, Building2,
} from "lucide-react";

const ROLE_TONE = {
  super_admin: "bg-rose-100 text-rose-800",
  partner_admin: "bg-indigo-100 text-indigo-800",
  partner: "bg-sky-100 text-sky-800",
};

function Pill({ children, tone = "bg-slate-100 text-slate-700" }) {
  return <span className={`inline-flex rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${tone}`}>{children}</span>;
}

function Stat({ label, value, tone }) {
  return (
    <div className="rounded-md border border-slate-200 bg-white px-3 py-2">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-lg font-semibold ${tone || "text-slate-800"}`}>{value}</div>
    </div>
  );
}

function UserTable({ rows, testid, emptyText }) {
  if (!rows?.length) return <div className="px-3 py-4 text-sm text-slate-500" data-testid={`${testid}-empty`}>{emptyText}</div>;
  return (
    <table className="w-full text-left text-sm" data-testid={testid}>
      <thead className="text-[11px] uppercase tracking-wide text-slate-500">
        <tr>
          <th className="px-3 py-1.5">Email</th>
          <th className="px-3 py-1.5">Name</th>
          <th className="px-3 py-1.5">Role</th>
          <th className="px-3 py-1.5">Status</th>
          <th className="px-3 py-1.5">Organization</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((u) => (
          <tr key={u.email} className="border-t border-slate-100">
            <td className="px-3 py-1.5 font-mono text-[12px] text-slate-800">{u.email}</td>
            <td className="px-3 py-1.5 text-slate-700">{u.name || "—"}</td>
            <td className="px-3 py-1.5"><Pill tone={ROLE_TONE[u.role]}>{u.role || "—"}</Pill></td>
            <td className="px-3 py-1.5 text-slate-600">{u.status || "—"}</td>
            <td className="px-3 py-1.5 text-slate-600">{u.org || <span className="text-slate-400">internal</span>}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function OrgTable({ rows, testid, emptyText }) {
  if (!rows?.length) return <div className="px-3 py-4 text-sm text-slate-500" data-testid={`${testid}-empty`}>{emptyText}</div>;
  return (
    <table className="w-full text-left text-sm" data-testid={testid}>
      <thead className="text-[11px] uppercase tracking-wide text-slate-500">
        <tr>
          <th className="px-3 py-1.5">Organization</th>
          <th className="px-3 py-1.5">Territory</th>
          <th className="px-3 py-1.5">Type</th>
          <th className="px-3 py-1.5">Status</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((o) => (
          <tr key={o.name} className="border-t border-slate-100">
            <td className="px-3 py-1.5 text-slate-800">{o.name}</td>
            <td className="px-3 py-1.5 font-mono text-[12px] text-slate-600">{o.territory || "—"}</td>
            <td className="px-3 py-1.5 text-slate-600">{o.partner_type || "—"}</td>
            <td className="px-3 py-1.5 text-slate-600">{o.status || "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function DiffTable({ rows, keyName, testid }) {
  if (!rows?.length) return <div className="px-3 py-4 text-sm text-slate-500" data-testid={`${testid}-empty`}>Everything that exists in both environments matches.</div>;
  return (
    <table className="w-full text-left text-sm" data-testid={testid}>
      <thead className="text-[11px] uppercase tracking-wide text-slate-500">
        <tr>
          <th className="px-3 py-1.5">{keyName}</th>
          <th className="px-3 py-1.5">Field</th>
          <th className="px-3 py-1.5">Here</th>
          <th className="px-3 py-1.5">Other environment</th>
        </tr>
      </thead>
      <tbody>
        {rows.flatMap((r) =>
          r.deltas.map((d, i) => (
            <tr key={`${r[keyName]}-${d.field}`} className="border-t border-slate-100">
              <td className="px-3 py-1.5 font-mono text-[12px] text-slate-800">{i === 0 ? r[keyName] : ""}</td>
              <td className="px-3 py-1.5 text-slate-600">{d.field}</td>
              <td className="px-3 py-1.5 text-emerald-800">{d.here || "—"}</td>
              <td className="px-3 py-1.5 text-amber-800">{d.there || "—"}</td>
            </tr>
          )),
        )}
      </tbody>
    </table>
  );
}

function Section({ title, icon, count, children, testid }) {
  return (
    <Card className="overflow-hidden p-0" data-testid={testid}>
      <div className="flex items-center gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2">
        {icon}
        <span className="text-sm font-semibold text-slate-800">{title}</span>
        <span className="rounded bg-white px-1.5 py-0.5 text-[11px] font-semibold text-slate-600">{count}</span>
      </div>
      <div className="max-h-[420px] overflow-auto">{children}</div>
    </Card>
  );
}

export default function EnvDrift() {
  const [snapshot, setSnapshot] = useState(null);
  const [pasted, setPasted] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/env-snapshot");
        setSnapshot(data);
      } catch (e) {
        toast.error("Could not read this environment's snapshot");
      }
    })();
  }, []);

  const download = () => {
    if (!snapshot) return;
    const host = snapshot.env?.host || "environment";
    const blob = new Blob([JSON.stringify(snapshot, null, 2)], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `conduit-snapshot-${host}-${snapshot.generated_at?.slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const copy = async () => {
    if (!snapshot) return;
    try {
      await navigator.clipboard.writeText(JSON.stringify(snapshot));
      toast.success("Snapshot copied — paste it into the other environment");
    } catch (e) {
      toast.error("Clipboard blocked by the browser — use Download instead");
    }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setPasted(await f.text());
  };

  const compare = async () => {
    let parsed;
    try {
      parsed = JSON.parse(pasted);
    } catch (err) {
      toast.error("That isn't valid JSON — paste the whole snapshot file");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/env-drift", { snapshot: parsed });
      setResult(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Comparison failed");
    } finally {
      setBusy(false);
    }
  };

  const otherHost = result?.there?.host || "other environment";
  const sum = result?.summary;
  const totalDrift = useMemo(
    () => (sum ? Object.values(sum).reduce((a, b) => a + b, 0) : 0),
    [sum],
  );

  return (
    <div data-testid="env-drift-page">
      <PageHeader
        testid="env-drift-header"
        title="Environment Drift"
        titleAccessory={<GitCompareArrows className="ml-2 h-5 w-5 text-teal-600" />}
        subtitle="Preview and production keep separate databases. Export a snapshot here, paste the other environment's snapshot below, and see exactly which accounts and organizations exist in one but not the other. Snapshots carry no passwords or tokens."
      />

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="p-4" data-testid="env-drift-this">
          <div className="mb-2 text-sm font-semibold text-slate-800">This environment</div>
          <div className="mb-3 font-mono text-[12px] text-slate-600" data-testid="env-drift-this-host">
            {snapshot?.env?.host || "…"}
          </div>
          <div className="mb-3 grid grid-cols-2 gap-2">
            <Stat label="Users" value={snapshot?.counts?.users ?? "—"} />
            <Stat label="Organizations" value={snapshot?.counts?.orgs ?? "—"} />
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={download} disabled={!snapshot} data-testid="env-drift-download" className="gap-1">
              <Download className="h-3.5 w-3.5" /> Download snapshot
            </Button>
            <Button size="sm" variant="outline" onClick={copy} disabled={!snapshot} data-testid="env-drift-copy" className="gap-1">
              <ClipboardCopy className="h-3.5 w-3.5" /> Copy JSON
            </Button>
          </div>
        </Card>

        <Card className="p-4" data-testid="env-drift-other">
          <div className="mb-2 text-sm font-semibold text-slate-800">Other environment&apos;s snapshot</div>
          <textarea
            data-testid="env-drift-paste"
            value={pasted}
            onChange={(e) => setPasted(e.target.value)}
            placeholder='Paste the JSON from the other environment (Settings → Environment Drift → Copy JSON), or load the downloaded file.'
            className="h-28 w-full rounded border border-slate-300 bg-white p-2 font-mono text-[11px]"
          />
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <input ref={fileRef} type="file" accept="application/json" onChange={onFile} className="hidden" data-testid="env-drift-file-input" />
            <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} data-testid="env-drift-file" className="gap-1">
              <Upload className="h-3.5 w-3.5" /> Load file
            </Button>
            <Button size="sm" onClick={compare} disabled={busy || !pasted.trim()} data-testid="env-drift-compare" className="gap-1">
              {busy ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Comparing…</> : <><GitCompareArrows className="h-3.5 w-3.5" /> Compare</>}
            </Button>
          </div>
        </Card>
      </div>

      {result && (
        <div className="mt-5 space-y-4" data-testid="env-drift-result">
          <div className="grid gap-2 sm:grid-cols-3 lg:grid-cols-6">
            <Stat label={`Users only here`} value={sum.users_only_here} tone="text-emerald-700" />
            <Stat label={`Users only there`} value={sum.users_only_there} tone="text-amber-700" />
            <Stat label="Users differing" value={sum.users_differ} tone="text-indigo-700" />
            <Stat label="Orgs only here" value={sum.orgs_only_here} tone="text-emerald-700" />
            <Stat label="Orgs only there" value={sum.orgs_only_there} tone="text-amber-700" />
            <Stat label="Orgs differing" value={sum.orgs_differ} tone="text-indigo-700" />
          </div>
          <div className="text-xs text-slate-500" data-testid="env-drift-context">
            {result.here.host} ({result.here.counts.users} users) vs {otherHost} ({result.there.counts?.users ?? "?"} users)
            {result.there.generated_at ? ` · their snapshot taken ${new Date(result.there.generated_at).toLocaleString()}` : ""}
            {totalDrift === 0 ? " · no drift found" : ""}
          </div>

          <Section
            title={`Users missing in ${otherHost}`}
            icon={<Users className="h-4 w-4 text-emerald-600" />}
            count={sum.users_only_here}
            testid="env-drift-users-only-here"
          >
            <UserTable rows={result.users.only_here} testid="env-drift-users-only-here-table"
                       emptyText={`Every user here also exists in ${otherHost}.`} />
          </Section>

          <Section
            title={`Users only in ${otherHost}`}
            icon={<Users className="h-4 w-4 text-amber-600" />}
            count={sum.users_only_there}
            testid="env-drift-users-only-there"
          >
            <UserTable rows={result.users.only_there} testid="env-drift-users-only-there-table"
                       emptyText="No extra users on the other side." />
          </Section>

          <Section
            title="Users that exist in both but differ"
            icon={<Users className="h-4 w-4 text-indigo-600" />}
            count={sum.users_differ}
            testid="env-drift-users-differ"
          >
            <DiffTable rows={result.users.differ} keyName="email" testid="env-drift-users-differ-table" />
          </Section>

          <Section
            title={`Organizations missing in ${otherHost}`}
            icon={<Building2 className="h-4 w-4 text-emerald-600" />}
            count={sum.orgs_only_here}
            testid="env-drift-orgs-only-here"
          >
            <OrgTable rows={result.orgs.only_here} testid="env-drift-orgs-only-here-table"
                      emptyText={`Every organization here also exists in ${otherHost}.`} />
          </Section>

          <Section
            title={`Organizations only in ${otherHost}`}
            icon={<Building2 className="h-4 w-4 text-amber-600" />}
            count={sum.orgs_only_there}
            testid="env-drift-orgs-only-there"
          >
            <OrgTable rows={result.orgs.only_there} testid="env-drift-orgs-only-there-table"
                      emptyText="No extra organizations on the other side." />
          </Section>

          <Section
            title="Organizations that exist in both but differ"
            icon={<Building2 className="h-4 w-4 text-indigo-600" />}
            count={sum.orgs_differ}
            testid="env-drift-orgs-differ"
          >
            <DiffTable rows={result.orgs.differ} keyName="name" testid="env-drift-orgs-differ-table" />
          </Section>
        </div>
      )}
    </div>
  );
}
